import React, { forwardRef, useEffect, useState } from 'react';
import { CoachStatusEnum } from 'types/generated/client';
import { LocationType, VenueAddress } from 'utils/shared/coachBuilder';
import { addOneHour } from 'utils/shared/date/addHourToSlot';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import CalendarDatePicker from 'components/CalendarDatePicker';
import classNames from 'styles/utils/classNames';
import CreateAccount from './CreateAccount';
import Login from './Login';
import PaymentForm from './PaymentForm';
import ResultForm from './ResultForm';
import { Coach, CurrentDateTimeState, CurrentVenueCourtState, Lessons, Steps } from '../types';

interface LessonSchedulerProps {
  coach: Coach;
  lessons?: Lessons;
  fromCalendar?: boolean;
  venueFromMap?: VenueAddress | null;
  initialCurrentDateTime?: CurrentDateTimeState;
  initialSelectedCourtVenue?: CurrentVenueCourtState;
  initialBookedLessonId?: string;
  setVenueFromMap?: React.Dispatch<React.SetStateAction<VenueAddress | null>>;
  showTabSwitch?: boolean;
  setShowTabSwitch?: (showTabSwitch: boolean) => void;
  handleBookAnotherLesson?: () => void;
  onPaymentSuccess?: () => void;
}

const LessonScheduler = ({
  coach,
  lessons,
  venueFromMap,
  setVenueFromMap,
  showTabSwitch,
  setShowTabSwitch,
  fromCalendar = false,
  initialCurrentDateTime,
  initialSelectedCourtVenue,
  initialBookedLessonId,
  handleBookAnotherLesson,
  onPaymentSuccess,
}: LessonSchedulerProps) => {
  const viewer = useViewer();
  const { user } = useGetCurrentUser();

  const [steps, setSteps] = useState<Steps>(
    fromCalendar
      ? viewer.isAnonymousSession
        ? Steps.CreateAccount
        : Steps.PaymentForm
      : Steps.SelectTime,
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [bookedLessonId, setBookedLessonId] = useState<string | null>(null);
  const [currentDateTime, setCurrentDateTime] = useState<CurrentDateTimeState>({
    date: new Date(),
    startTime: '',
    endTime: '',
  });

  const [selectedCourtVenue, setSelectedCourtVenue] = useState<CurrentVenueCourtState>({
    id: '',
    title: '',
    address: '',
    locationType: LocationType.Venue,
  });
  const isCoach = user?.coachStatus === CoachStatusEnum.Active;

  const handleLessonSchedule = (
    day: Date,
    timeslot: string,
    courtVenue: CurrentVenueCourtState | null,
  ) => {
    if (viewer.isAnonymousSession) {
      setSteps(Steps.CreateAccount);
    } else {
      setSteps(Steps.PaymentForm);
    }

    setCurrentDateTime((prevState) => ({
      ...prevState,
      date: day,
      startTime: timeslot,
      endTime: addOneHour(timeslot),
    }));

    setSelectedCourtVenue((prevState) => ({
      ...prevState,
      id: courtVenue?.id || '',
      title: courtVenue?.title || '',
      address: courtVenue?.address || '',
      locationType: courtVenue?.locationType!,
    }));
  };

  useEffect(() => {
    if (!venueFromMap) return;
    setSteps(Steps.SelectTime);
  }, [venueFromMap]);

  useEffect(() => {
    setCurrentDateTime({
      ...currentDateTime,
      date: selectedDate,
    });
  }, [selectedDate]);

  useEffect(() => {
    if (setShowTabSwitch) {
      if (steps === Steps.SelectTime) {
        setShowTabSwitch(true);
      } else {
        setShowTabSwitch(false);
      }
    }
  }, [steps, setShowTabSwitch]);

  useEffect(() => {
    if (
      fromCalendar &&
      initialCurrentDateTime &&
      initialSelectedCourtVenue &&
      initialBookedLessonId
    ) {
      setCurrentDateTime(initialCurrentDateTime);
      setSelectedCourtVenue(initialSelectedCourtVenue);
      setBookedLessonId(initialBookedLessonId);
    }
  }, [fromCalendar, initialCurrentDateTime, initialSelectedCourtVenue, initialBookedLessonId]);

  return (
    <div className="rounded-xl bg-color-bg-lightmode-primary  dark:bg-color-bg-darkmode-primary">
      {fromCalendar === false && steps === Steps.SelectTime && (
        <CalendarDatePicker
          selectTimeSlot={handleLessonSchedule}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          coach={coach}
          lessons={lessons || []}
          venueFromMap={venueFromMap}
          setVenueFromMap={setVenueFromMap}
          showTabSwitch={showTabSwitch}
          setShowTabSwitch={setShowTabSwitch}
          isCoach={isCoach}
        />
      )}
      {steps === Steps.CreateAccount && (
        <CreateAccount
          setSteps={setSteps}
          handleNext={() => {
            setSteps(Steps.PaymentForm);
          }}
        />
      )}
      {steps === Steps.Login && (
        <Login
          setSteps={setSteps}
          handleNext={() => {
            setSteps(Steps.PaymentForm);
          }}
        />
      )}
      {steps === Steps.PaymentForm && (
        <div className="pt-6">
          <PaymentForm
            setBookedLessonId={setBookedLessonId}
            setSteps={setSteps}
            currentDateTime={currentDateTime}
            selectedCourtVenue={selectedCourtVenue}
            coach={coach}
            allowBack={!fromCalendar}
            onPaymentSuccess={onPaymentSuccess}
          />
        </div>
      )}
      {steps === Steps.ResultForm && (
        <div className="py-5">
          <ResultForm
            bookedLessonId={bookedLessonId}
            setSteps={setSteps}
            currentDateTime={currentDateTime}
            coach={coach}
            selectedCourtVenue={selectedCourtVenue}
            handleBookAnotherLesson={handleBookAnotherLesson}
          />
        </div>
      )}
    </div>
  );
};

export default LessonScheduler;
